import React, { useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import BottomScrollListener from "react-bottom-scroll-listener";
import ApplyPurchaseToTrivvy from "./ApplyPurchaseToTrivvyModal";
import axios from "../../../axios";
import * as url from "../../../utils/url";
import { TRIVVYS_WITH_WAITING_RESPONSES_TITLE } from "../../../utils/constants";
import "../trivia-survey.css";
import "./WaitingResponsesMobile.css";
import "./menu-modals-bwai.css";

const PER_PAGE = 20;

// Lists every Trivvy (Survey, Knowledge Check OR Message) that has waiting
// responses. The server already filters to waitingCount > 0 and returns a
// `total`, so we paginate with infinite scroll (load the next page when the
// list is scrolled to the bottom) instead of fetching the whole catalogue.
function TrivvysWithWaitingResponsesModal({ isOpen, toggle }) {
  const [waitingTrivvys, setWaitingTrivvys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [applyPurchaseOpen, setApplyPurchaseOpen] = useState(false);
  const [applyPurchaseModalData, setApplyPurchaseModalData] = useState(null);

  // Refs mirror the load state so the scroll handler / overflow check read
  // fresh values (avoid stale closures) and can't fire overlapping requests.
  const isFetchingRef = useRef(false);
  const loadedCountRef = useRef(0);
  const totalRef = useRef(0);
  const trivvysRef = useRef([]);
  // The actual scrolling DOM node, so we can detect when the list doesn't yet
  // overflow (no scrollbar => no scroll event => onBottom can never fire).
  const scrollNodeRef = useRef(null);

  const fetchPage = (pageNum) => {
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    axios
      .get(url.getTrivvysWithWaitingResponses, {
        params: { page: pageNum, perPage: PER_PAGE },
      })
      .then(({ data }) => {
        const payload = data && data.data ? data.data : {};
        const surveys = payload.surveys ? payload.surveys : [];
        const totalCount =
          typeof payload.total === "number" ? payload.total : 0;
        // Build the next list outside the state updater so the ref bookkeeping
        // stays a plain, predictable side effect (not a render-phase mutation).
        const next =
          pageNum === 1 ? surveys : trivvysRef.current.concat(surveys);
        // If a non-first page comes back empty while `total` still claims more
        // (e.g. a trivvy was answered/deleted between find + countDocuments),
        // treat the list as exhausted so we don't re-request the same page.
        const exhausted = pageNum > 1 && surveys.length === 0;
        trivvysRef.current = next;
        loadedCountRef.current = next.length;
        totalRef.current = exhausted ? next.length : totalCount;
        setWaitingTrivvys(next);
        setLoading(false);
        isFetchingRef.current = false;
      })
      .catch((err) => {
        console.log(err);
        setErrorMsg("Unable to load trivvys with waiting responses.");
        setLoading(false);
        isFetchingRef.current = false;
      });
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Fresh state on each open.
    setWaitingTrivvys([]);
    setErrorMsg("");
    trivvysRef.current = [];
    loadedCountRef.current = 0;
    totalRef.current = 0;
    isFetchingRef.current = false;
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Safety net for tall viewports: if the loaded rows don't fill the scroll
  // container there is no scrollbar, so onBottom can never fire. Keep pulling
  // the next page until the list overflows or everything is loaded.
  useEffect(() => {
    if (!isOpen || loading || errorMsg) {
      return;
    }
    if (loadedCountRef.current >= totalRef.current) {
      return;
    }
    const node = scrollNodeRef.current;
    if (node && node.scrollHeight <= node.clientHeight) {
      const nextPage = Math.floor(loadedCountRef.current / PER_PAGE) + 1;
      fetchPage(nextPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingTrivvys, loading, errorMsg, isOpen]);

  const handleBottom = () => {
    if (isFetchingRef.current) {
      return;
    }
    if (loadedCountRef.current >= totalRef.current) {
      return; // everything loaded
    }
    const nextPage = Math.floor(loadedCountRef.current / PER_PAGE) + 1;
    fetchPage(nextPage);
  };

  // Bind the scroll node to both the library's ref (so it can listen) and our
  // own ref (so the overflow check above can measure it).
  const setScrollNode = (scrollRef) => (node) => {
    if (typeof scrollRef === "function") {
      scrollRef(node);
    } else if (scrollRef) {
      scrollRef.current = node;
    }
    scrollNodeRef.current = node;
  };

  const openApplyPurchaseModal = (trivvy) => {
    setApplyPurchaseModalData(trivvy);
    setApplyPurchaseOpen(true);
  };

  const closeApplyPurchaseModal = () => {
    setApplyPurchaseOpen(false);
    setApplyPurchaseModalData(null);
  };

  // Called by ApplyPurchaseToTrivvy after a successful purchase. A partially
  // applied trivvy stays in the list with a lower count; a fully cleared one
  // (waitingCount 0) drops out of the server's waitingCount > 0 set, so we
  // reset and re-sync from page 1 (also avoids client/server pagination drift).
  const handleApplyPurchaseRowUpdate = (updated) => {
    if (!updated || !updated._id) {
      return;
    }
    if (updated.waitingCount > 0) {
      const next = trivvysRef.current.map((trivvy) =>
        trivvy._id === updated._id
          ? { ...trivvy, waitingCount: updated.waitingCount }
          : trivvy
      );
      trivvysRef.current = next;
      setWaitingTrivvys(next);
      return;
    }
    trivvysRef.current = [];
    loadedCountRef.current = 0;
    totalRef.current = 0;
    isFetchingRef.current = false;
    setWaitingTrivvys([]);
    fetchPage(1);
  };

  const renderRows = () =>
    waitingTrivvys.map((trivvy) => {
      const count = trivvy.waitingCount;
      const label = count === 1 ? "response waiting" : "responses waiting";
      return (
        <div key={trivvy._id} className="shared-users-list-item bwai_wr_row">
          <span className="trivvys_waiting_name bwai_wr_name">
            {trivvy.name}
          </span>
          <span className="waiting_text bwai_wr_count">
            {count} {label}
          </span>
          <Button
            size="sm"
            className="trivia-action-button-main fill trivvys_waiting_manage_btn bwai_wr_btn"
            onClick={() => openApplyPurchaseModal(trivvy)}
          >
            Manage Purchase
          </Button>
        </div>
      );
    });

  const renderBody = () => {
    if (errorMsg) {
      return <div className="text-danger">{errorMsg}</div>;
    }
    if (!loading && waitingTrivvys.length === 0) {
      return (
        <div className="bwai_wr_empty">No trivvys with waiting responses.</div>
      );
    }
    return (
      <BottomScrollListener offset={80} onBottom={handleBottom}>
        {(scrollRef) => (
          <div
            ref={setScrollNode(scrollRef)}
            className="trivvys_waiting_modal_scroll"
          >
            {renderRows()}
            {loading && (
              <div className="text-center py-2 text-muted bwai_wr_loading">
                Loading...
              </div>
            )}
          </div>
        )}
      </BottomScrollListener>
    );
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        className="trivvys-waiting-modal bwai-modal bwai_menu_modal bwai_waiting_responses"
      >
        <ModalHeader className="bwai_modal_header" toggle={toggle}>
          {TRIVVYS_WITH_WAITING_RESPONSES_TITLE}
        </ModalHeader>
        <ModalBody>{renderBody()}</ModalBody>
        <ModalFooter>
          <Button
            size="sm"
            className="trivia-action-button-main-secondary"
            onClick={toggle}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      {applyPurchaseOpen && applyPurchaseModalData && (
        <ApplyPurchaseToTrivvy
          open={applyPurchaseOpen}
          onClose={closeApplyPurchaseModal}
          handleUpdateRow={handleApplyPurchaseRowUpdate}
          trivia={applyPurchaseModalData}
        />
      )}
    </React.Fragment>
  );
}

export default TrivvysWithWaitingResponsesModal;
